<template>
  <figure
    class="HeightRangeChart"
    data-theme="light"
    :style="{
      '--barCount': barItems.length,
    }"
  >
    <figcaption class="HeightRangeChart__title">
      Height range
    </figcaption>
    <TransitionGroup
      class="HeightRangeChart__chart"
      tag="div"
      appear
    >
      <div
        v-for="heightItem in barItems"
        :key="heightItem.height"
        class="HeightRangeChart__bar"
        data-testid="bar"
      >
        <div
          class="HeightRangeChart__barValue"
          data-testid="barValue"
          :style="{
            '--barPercent': `${(1 - heightItem.ratio) * 100}%`,
          }"
        />
      </div>
    </TransitionGroup>
    <div class="HeightRangeChart__labelGrid">
      <div
        v-for="(labelItem, i) in labelItems"
        :key="labelItem.height"
        class="HeightRangeChart__labelColumn"
        data-testid="labelColumn"
        :style="{
          gridColumnStart: 1 + i * divisor,
        }"
      >
        <div
          class="HeightRangeChart__label"
          data-testid="label"
        >
          {{ formatHeight(labelItem.height, { includeInches: false }) }}
        </div>
      </div>
    </div>
  </figure>
</template>

<script lang="ts" setup>
import formatHeight from "@/lib/utils/string/formatHeight";
import { PlayerHeightCount } from "@shared/lib/database/query.types";
import { computed } from "vue";

const heightCounts = defineModel<Array<PlayerHeightCount>>({
  required: true,
});

const evenMinHeight = computed(() => {
  const minHeight = Math.min(...heightCounts.value.map((heightCount) => heightCount.height));

  return roundToNearest(minHeight, 2, Math.floor);
});

const evenMaxHeight = computed(() => {
  const maxHeight = Math.max(...heightCounts.value.map((heightCount) => heightCount.height));

  return roundToNearest(maxHeight, 2, Math.ceil);
});

const divisor = computed(() => {
  const _divisor = Math.ceil((evenMaxHeight.value - evenMinHeight.value) / 4);

  return roundToNearest(_divisor, 2, Math.ceil) || 1;
});

const startHeight = computed(() => {
  return roundToNearest(evenMinHeight.value, divisor.value, Math.floor);
});

const endHeight = computed(() => {
  return roundToNearest(evenMaxHeight.value, divisor.value, Math.ceil);
});

const maxCount = computed(() => {
  return Math.max(...heightCounts.value.map((heightCount) => heightCount.count)) || 1;
});

const barItems = computed(() => {
  return Array.from({ length: endHeight.value - startHeight.value + 1 }, (_, index) => {
    const height = startHeight.value + index;
    const count = heightCounts.value.find((heightCount) => heightCount.height === height)?.count ?? 0;

    return {
      height,
      count,
      ratio: count / maxCount.value,
    };
  });
});

const labelItems = computed(() => {
  return Array.from({ length: (endHeight.value - startHeight.value) / divisor.value + 1 }, (_, index) => {
    const height = startHeight.value + index * divisor.value;

    return {
      height,
    };
  });
});

function roundToNearest (value: number, count: number, roundFn: (value: number) => number) {
  return roundFn(value / count) * count;
}
</script>

<style lang="scss" scoped>
.HeightRangeChart {
  display: grid;
  padding: 20px 16px 16px;
  background-color: var(--themeColorSurface);
}

.HeightRangeChart__title {
  margin-bottom: 20px;

  font-size: 14px;
  font-weight: var(--fontWeightMedium);
  text-align: center;
  text-transform: uppercase;
  line-height: 16px;
}

.HeightRangeChart__chart {
  display: flex;
  gap: 4px;
  height: 72px;
}

.HeightRangeChart__bar {
  display: flex;
  flex: 1 1 auto;
  justify-content: center;
  overflow: hidden;

  border-bottom: 1px solid var(--themeColorNeutral100);

  transition: none 350ms;
}

.HeightRangeChart__barValue {
  transform: translateY(var(--barPercent));
  transform-origin: bottom;

  width: 100%;
  max-width: 180px;
  height: 100%;

  border-radius: 4px 4px 0 0;
  background-color: var(--themeColorSecondary);

  transition: 350ms transform;

  .HeightRangeChart__bar.v-enter-from & {
    transform: translateY(100%);
  }
}

.HeightRangeChart__labelGrid {
  position: relative;

  display: grid;
  grid-template-columns: repeat(var(--barCount), 1fr);
  gap: 4px;
  margin-top: 12px;
}

.HeightRangeChart__labelColumn {
  position: relative;

  height: 1lh;

  font: var(--fontWeightNormal) 13px/1 var(--fontFamilyMono);
  text-align: center;
  white-space: nowrap;
}

.HeightRangeChart__label {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}
</style>
